import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Sparkles, Check, X, Copy, Send, ArrowRight, Loader2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { apiRequest } from "@/lib/queryClient";
import type { PromptEnhancerResult } from "@shared/schema";

interface PromptEnhancerProps {
    prompt: string;
    onEnhanced: (enhancedPrompt: string) => void;
    onSendEnhanced: (enhancedPrompt: string) => void;
    onClose: () => void;
}

interface DynamicQuestion {
    id: string;
    question: string;
    choices: string[];
}

interface UserAnswers {
    selectedChoices: Record<string, string[]>;
    additionalText: string;
}

type EnhancerState = 
    | { phase: "loading-questions" }
    | { phase: "questioning"; questions: DynamicQuestion[]; answers: UserAnswers }
    | { phase: "creating" }
    | { phase: "done"; result: PromptEnhancerResult };

export function PromptEnhancer({ prompt, onEnhanced, onSendEnhanced, onClose }: PromptEnhancerProps) {
    const [state, setState] = useState<EnhancerState>({ phase: "loading-questions" });
    const [copied, setCopied] = useState(false);

    const generateQuestionsMutation = useMutation({
        mutationFn: async (userPrompt: string) => {
            const response = await apiRequest("POST", "/api/prompt-enhancer/questions", { prompt: userPrompt });
            return response.json();
        },
        onSuccess: (data) => {
            if (data.questions && data.questions.length > 0) {
                setState({
                    phase: "questioning",
                    questions: data.questions,
                    answers: { selectedChoices: {}, additionalText: "" }
                });
            } else {
                setState({
                    phase: "questioning",
                    questions: getDefaultQuestions(),
                    answers: { selectedChoices: {}, additionalText: "" }
                });
            }
        },
        onError: () => {
            setState({
                phase: "questioning",
                questions: getDefaultQuestions(),
                answers: { selectedChoices: {}, additionalText: "" }
            });
        }
    });

    const createPromptMutation = useMutation({
        mutationFn: async ({ originalPrompt, questions, answers }: { 
            originalPrompt: string; 
            questions: DynamicQuestion[];
            answers: UserAnswers 
        }) => {
            const clarifications = questions.map(q => {
                const choices = answers.selectedChoices[q.id] || [];
                return {
                    questionId: q.id,
                    question: q.question,
                    answer: choices.join(", ")
                };
            }).filter(c => c.answer);

            if (answers.additionalText.trim()) {
                clarifications.push({
                    questionId: "additional",
                    question: "Additional context",
                    answer: answers.additionalText.trim()
                });
            }

            const response = await apiRequest("POST", "/api/prompt-enhancer/create", { 
                prompt: originalPrompt,
                clarifications
            });
            return response.json();
        },
        onSuccess: (data) => {
            if (data.enhancedPrompt) {
                setState({
                    phase: "done",
                    result: data as PromptEnhancerResult
                });
            }
        },
        onError: (error) => {
            console.error("Prompt creation failed:", error);
        }
    });

    useEffect(() => {
        if (prompt.trim()) {
            generateQuestionsMutation.mutate(prompt);
        }
    }, []);

    const getDefaultQuestions = (): DynamicQuestion[] => [
        {
            id: "detail",
            question: "How detailed should the response be?",
            choices: ["Brief overview", "Moderate detail", "Comprehensive", "Expert deep-dive"]
        },
        {
            id: "format",
            question: "What format works best?",
            choices: ["Step-by-step", "Bullet points", "Paragraph explanation", "Code examples", "Comparison"]
        },
        {
            id: "focus",
            question: "What aspect is most important?",
            choices: ["Getting started quickly", "Understanding concepts", "Best practices", "Practical examples"]
        }
    ];

    const handleChoiceToggle = (questionId: string, choice: string) => {
        if (state.phase !== "questioning") return;
        
        const currentChoices = state.answers.selectedChoices[questionId] || [];
        const isSelected = currentChoices.includes(choice);
        
        setState({
            ...state,
            answers: {
                ...state.answers,
                selectedChoices: {
                    ...state.answers.selectedChoices,
                    [questionId]: isSelected 
                        ? currentChoices.filter(c => c !== choice)
                        : [...currentChoices, choice]
                }
            }
        });
    };

    const handleAdditionalTextChange = (text: string) => {
        if (state.phase !== "questioning") return;
        setState({
            ...state,
            answers: {
                ...state.answers,
                additionalText: text
            }
        });
    };

    const handleCreatePrompt = () => {
        if (state.phase !== "questioning") return;
        setState({ phase: "creating" });
        createPromptMutation.mutate({ 
            originalPrompt: prompt,
            questions: state.questions,
            answers: state.answers 
        });
    };

    const handleCopy = () => {
        if (state.phase !== "done") return;
        navigator.clipboard.writeText(state.result.enhancedPrompt);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleReplace = () => {
        if (state.phase !== "done") return;
        onEnhanced(state.result.enhancedPrompt);
        onClose();
    };

    const handleSendNow = () => {
        if (state.phase !== "done") return;
        onSendEnhanced(state.result.enhancedPrompt);
        onClose();
    };

    const getSelectedCount = (): number => {
        if (state.phase !== "questioning") return 0;
        return Object.values(state.answers.selectedChoices)
            .reduce((total, choices) => total + choices.length, 0);
    };

    return (
        <div className="border-t bg-muted/30 p-3 space-y-3" data-testid="prompt-enhancer">
            {state.phase === "loading-questions" && (
                <div className="flex items-center justify-center py-4 gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Analyzing your request...</span>
                </div>
            )}

            {state.phase === "creating" && (
                <div className="flex items-center justify-center py-4 gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Creating your enhanced prompt...</span>
                </div>
            )}

            {state.phase === "questioning" && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium">Quick refinements</span>
                        </div>
                        {getSelectedCount() > 0 && (
                            <Badge variant="secondary" className="text-xs">
                                {getSelectedCount()} selected
                            </Badge>
                        )}
                    </div>

                    <div className="space-y-4">
                        {state.questions.map((q) => {
                            const selectedChoices = state.answers.selectedChoices[q.id] || [];
                            
                            return (
                                <div key={q.id} className="space-y-2">
                                    <p className="text-sm font-medium text-muted-foreground">{q.question}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {q.choices.map((choice) => {
                                            const isSelected = selectedChoices.includes(choice);
                                            return (
                                                <Badge
                                                    key={choice}
                                                    variant={isSelected ? "default" : "outline"}
                                                    className="cursor-pointer hover-elevate text-xs py-1"
                                                    onClick={() => handleChoiceToggle(q.id, choice)}
                                                    data-testid={`choice-${q.id}-${choice.replace(/\s+/g, '-').toLowerCase()}`}
                                                >
                                                    {isSelected && <Check className="h-3 w-3 mr-1" />}
                                                    {choice}
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <Textarea
                        placeholder="Any specific requirements or context to add..."
                        value={state.answers.additionalText}
                        onChange={(e) => handleAdditionalTextChange(e.target.value)}
                        className="min-h-[50px] text-sm resize-none"
                        data-testid="input-additional-context"
                    />

                    <div className="flex items-center justify-between pt-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        
                        <Button
                            size="sm"
                            onClick={handleCreatePrompt}
                            data-testid="button-create-prompt-now"
                        >
                            <Sparkles className="h-4 w-4 mr-1" />
                            Create Prompt Now
                        </Button>
                    </div>
                </div>
            )}

            {state.phase === "done" && (
                <div className="space-y-3">
                    <Card className="p-3 bg-primary/5 border-primary/20">
                        <div className="flex items-start gap-2">
                            <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-primary mb-1">Ready to send</p>
                                <p className="text-sm whitespace-pre-wrap" data-testid="text-enhanced-prompt">
                                    {state.result.enhancedPrompt}
                                </p>
                            </div>
                        </div>
                    </Card>

                    <div className="flex items-center justify-between">
                        <Button variant="ghost" size="sm" onClick={onClose}>
                            <X className="h-4 w-4 mr-1" />
                            Discard
                        </Button>
                        <div className="flex items-center gap-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={handleCopy}
                                        data-testid="button-copy-enhanced"
                                    >
                                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Copy to clipboard</TooltipContent>
                            </Tooltip>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={handleReplace}
                                data-testid="button-replace-prompt"
                            >
                                Replace Input
                            </Button>
                            <Button 
                                size="sm" 
                                onClick={handleSendNow}
                                data-testid="button-send-enhanced"
                            >
                                <Send className="h-4 w-4 mr-1" />
                                Send Now
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
